import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import SponsorMultiAdv from "core/components/SponsorMultiAdv";
import { usePlayerRsya } from "core/components/Player/PlayerRsyaContext";
import getBrandingStartSecond, {
  getBrandingDurationBucket,
} from "core/utils/inStreamBrandingSchedule";
import {
  trackBrandingEvent,
  BRANDING_SHORT_VIDEO,
  BRANDING_OPPORTUNITY,
  BRANDING_FILLED,
  BRANDING_NO_FILL,
} from "core/utils/adAnalytics";

/**
 * Инстрим-брендинг / баннер-уголок внутри плеера.
 *
 * Один показ за просмотр: секунда показа выбирается по длительности видео
 * (core/utils/inStreamBrandingSchedule), считаются только секунды
 * воспроизведения. Преролл идёт до монтирования экрана видео, поэтому в отсчёт
 * не попадает. Повторов нет ни в видео, ни в трансляциях.
 *
 * Старт рекламы Яндекса (РСЯ) считается завершением показа: брендинг на экране
 * закрывается и в этом просмотре не возвращается, а если реклама стартовала до
 * секунды показа, он не начнётся.
 */
export default function SponsorMidAdv({
  videoState,
  duration,
  isLive,
  contentId,
  rightholderId,
  sponsorInStreamBannerPlace,
  sponsorCornerBannerPlace,
  fullscreenTarget,
  setInStreamBannerHeight,
  setCornerBannerHeight,
}) {
  const [playedTime, setPlayedTime] = useState(0);
  const [shown, setShown] = useState(false);
  const { adStarted } = usePlayerRsya();
  // Закрыто производно: пока идёт реклама Яндекса, брендинг не монтируется ни
  // в одном рендере. Эффект ниже делает закрытие липким после конца рекламы.
  const closed = shown || adStarted;

  useEffect(() => {
    if (adStarted) setShown(true);
  }, [adStarted]);

  // undefined: ещё не решили (ждём длительность), null: в этом просмотре не показываем
  const startSecondRef = useRef(undefined);
  if (startSecondRef.current === undefined) {
    startSecondRef.current = getBrandingStartSecond({ duration, isLive });
  }

  const startSecond = startSecondRef.current;
  const durationBucket = getBrandingDurationBucket({ duration, isLive });
  const reached =
    typeof startSecond === "number" && playedTime >= startSecond && !closed;

  // Аналитика инвентаря: по одному событию на просмотр
  const shortTrackedRef = useRef(false);
  const opportunityTrackedRef = useRef(false);
  // Запрос кампании может отработать несколько раз за просмотр (перемонтирование
  // после конца видео, повторный ответ сети): в отчёт идёт только первый ответ
  const fillTrackedRef = useRef(false);

  useEffect(() => {
    if (!process.browser || startSecond !== null || shortTrackedRef.current) {
      return;
    }
    shortTrackedRef.current = true;
    trackBrandingEvent(BRANDING_SHORT_VIDEO, {
      contentId,
      durationBucket,
    });
  }, [startSecond]);

  useEffect(() => {
    if (!reached || opportunityTrackedRef.current) return;
    opportunityTrackedRef.current = true;
    trackBrandingEvent(BRANDING_OPPORTUNITY, {
      contentId,
      durationBucket,
    });
  }, [reached]);

  const onCompleted = (data) => {
    if (fillTrackedRef.current) return;
    fillTrackedRef.current = true;

    const campaign = data?.sponsorCampaign;
    const format = campaign?.cornerBanner
      ? "frame"
      : campaign?.inStreamBanner
      ? "strip"
      : undefined;

    trackBrandingEvent(format ? BRANDING_FILLED : BRANDING_NO_FILL, {
      contentId,
      durationBucket,
      format,
    });
  };
  const shouldCount =
    process.browser &&
    !closed &&
    typeof startSecond === "number" &&
    playedTime < startSecond &&
    videoState.status === "playing";

  useEffect(() => {
    if (!shouldCount) return undefined;

    const timer = setInterval(() => {
      setPlayedTime((t) => t + 1);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [shouldCount]);

  if (!process.browser) return null;
  if (closed) return null;
  if (videoState.isEnded) return null;
  if (typeof startSecond !== "number") return null;
  if (playedTime < startSecond) return null;

  const onFinish = () => {
    setShown(true);
  };

  return (
    <SponsorMultiAdv
      inStreamBannerPlace={sponsorInStreamBannerPlace}
      cornerBannerPlace={sponsorCornerBannerPlace}
      contentId={contentId}
      rightholderId={rightholderId}
      fullscreenTarget={fullscreenTarget}
      onFinish={onFinish}
      onCompleted={onCompleted}
      setInStreamBannerHeight={setInStreamBannerHeight}
      setCornerBannerHeight={setCornerBannerHeight}
    />
  );
}

SponsorMidAdv.propTypes = {
  videoState: PropTypes.shape({
    status: PropTypes.oneOf(["playing", "paused"]).isRequired,
    isEnded: PropTypes.bool,
  }).isRequired,
  duration: PropTypes.number,
  isLive: PropTypes.bool,
  contentId: PropTypes.string.isRequired,
  rightholderId: PropTypes.string.isRequired,
  sponsorInStreamBannerPlace: PropTypes.string,
  sponsorCornerBannerPlace: PropTypes.string,
  fullscreenTarget: PropTypes.string.isRequired,
  setInStreamBannerHeight: PropTypes.func.isRequired,
  setCornerBannerHeight: PropTypes.func.isRequired,
};
